// Supabase implementation of EmailMethods (B5-T23): RPCs of 20261012130000_gavion_email_marketing.sql and
// the gavion-ses-send admin actions. Errors become EmailApiError(<stable code>): a P0001 message is the
// code; an edge function answers { error: <code> }. The browser never reads a table it cannot SELECT.
import type { SupabaseClient } from "@supabase/supabase-js";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";
import {
  AUTO_ENROLL_BLOCKERS,
  EmailApiError,
  toEmailErrorCode,
  type AutoEnrollBlocker,
  type CampaignRecord,
  type ConsentRecordRow,
  type EmailMethods,
  type EnrollmentRecord,
  type ProviderStatus,
  type SequenceStepStat,
  type SendTestResult,
  type StarterInstallResult,
} from "./types";
import {
  STARTER_SEQUENCES,
  starterTemplates,
} from "@/components/gavion/domain/email";
import { normalizeSequencePerformance } from "../sequences/performance";

type PgError = { message?: string; code?: string } | null;
const normalize = (e: string) => e.trim().toLowerCase();

/** An Identifier from react-admin as the bigint the RPC expects. */
const id = (v: unknown) =>
  typeof v === "string" && /^\d+$/.test(v) ? Number(v) : v;

async function functionErrorCode(error: unknown): Promise<string> {
  const ctx = (error as { context?: unknown } | null)?.context;
  if (ctx instanceof Response) {
    try {
      const body = (await ctx.clone().json()) as { error?: unknown };
      if (typeof body?.error === "string") return body.error;
    } catch {
      // not JSON: fall through
    }
  }
  return "unknown";
}

export function createSupabaseEmailMethods(
  client: SupabaseClient = getSupabaseClient(),
): EmailMethods {
  const fail = (code: string): never => {
    throw new EmailApiError(toEmailErrorCode(code));
  };
  async function rpc<T>(fn: string, args?: Record<string, unknown>) {
    const { data, error } = (await client.rpc(fn, args)) as {
      data: T;
      error: PgError;
    };
    if (error) fail(error.message ?? "");
    return data;
  }
  async function invoke<T>(body: Record<string, unknown>): Promise<T> {
    const { data, error } = await client.functions.invoke("gavion-ses-send", {
      body,
    });
    if (error) fail(await functionErrorCode(error));
    return data as T;
  }

  return {
    async emailPreviewSegment(entity, filter) {
      return rpc("preview_segment", { p_entity: entity, p_filter: filter });
    },
    async emailScheduleCampaign(campaignId, at) {
      return rpc<CampaignRecord>("schedule_campaign", {
        p_campaign_id: id(campaignId),
        p_at: at,
      });
    },
    async emailSetCampaignStatus(campaignId, action) {
      return rpc<CampaignRecord>("set_campaign_status", {
        p_campaign_id: id(campaignId),
        p_action: action,
      });
    },
    async emailEnroll({ sequenceId, companyId, contactId = null, email }) {
      const e = normalize(email);
      // enroll_in_sequence returns the existing active row as-is: the pre-check tells them apart.
      const { data: existing, error } = await client
        .from("sequence_enrollments")
        .select("id")
        .eq("sequence_id", id(sequenceId))
        .eq("email", e)
        .eq("status", "active")
        .limit(1);
      if (error) fail((error as PgError)?.message ?? "");
      const enrollment = await rpc<EnrollmentRecord>("enroll_in_sequence", {
        p_sequence_id: id(sequenceId),
        p_company_id: id(companyId),
        p_contact_id: contactId == null ? null : id(contactId),
        p_email: e,
        p_by: null,
      });
      return {
        enrollment,
        alreadyEnrolled: (existing?.length ?? 0) > 0,
      };
    },
    // GAVION video-campaign: « Ajouter à la campagne »
    async emailEnrollCompanies({ sequenceId, companyIds }) {
      const r = await rpc<Record<string, unknown> | null>(
        "enroll_companies_in_sequence",
        { p_sequence_id: id(sequenceId), p_company_ids: companyIds.map(id) },
      );
      const n = (k: string) => Number(r?.[k] ?? 0);
      return {
        enrolled: n("enrolled"),
        already: n("already"),
        status: n("status"),
        noEmail: n("no_email"),
        suppressed: n("suppressed"),
        noConsent: n("no_consent"),
        noRep: n("no_rep"),
        notConnected: n("not_connected"),
        notOwn: n("not_own"),
        failed: n("failed"),
      };
    },
    async emailUnenroll(enrollmentId) {
      await rpc("unenroll_sequence", { p_enrollment_id: id(enrollmentId) });
    },
    // GAVION sequence-auto-enroll: 99_gavion_sequence_auto_enroll.sql (admins)
    async emailSetSequenceAutoEnroll(sequenceId, settings) {
      await rpc("set_sequence_auto_enroll", {
        p_sequence_id: id(sequenceId),
        p_settings:
          settings == null
            ? null
            : {
                segment_id: id(settings.segmentId),
                per_day: settings.perDay,
                paused: settings.paused,
              },
      });
    },
    async emailSequenceAutoEnrollPreview(sequenceId, segmentId = null) {
      const r = await rpc<Record<string, unknown> | null>(
        "sequence_auto_enroll_preview",
        {
          p_sequence_id: id(sequenceId),
          p_segment_id: segmentId == null ? null : id(segmentId),
        },
      );
      const blocker = String(r?.blocker ?? "");
      return {
        candidates: r?.candidates == null ? null : Number(r.candidates),
        todayEnrolled: Number(r?.today_enrolled ?? 0),
        lastRun: r?.last_run == null ? null : String(r.last_run),
        paused: typeof r?.paused === "boolean" ? r.paused : null,
        pausedReason:
          r?.paused_reason === "segment_empty" || r?.paused_reason === "manual"
            ? r.paused_reason
            : null,
        blocker: (AUTO_ENROLL_BLOCKERS as readonly string[]).includes(blocker)
          ? (blocker as AutoEnrollBlocker)
          : null,
        capLeft: Number(r?.cap_left ?? 0),
      };
    },
    async emailRecordConsent(i) {
      return rpc<ConsentRecordRow>("record_consent", {
        p_email: normalize(i.email),
        p_basis: i.basis,
        p_evidence: i.evidence,
        p_obtained_at: i.obtainedAt,
        p_company_id: i.companyId == null ? null : id(i.companyId),
        p_contact_id: i.contactId == null ? null : id(i.contactId),
      });
    },
    // GAVION campaign-fixes: 94_gavion_campaign_fixes.sql (admins)
    async emailRecordPublicationConsents(i) {
      const r = await rpc<Record<string, unknown> | null>(
        "record_publication_consents",
        {
          p_company_ids: i.companyIds.map(id),
          p_source: i.source,
          p_url: i.url?.trim() || null,
          p_via: i.via ?? "bulk",
        },
      );
      return {
        recorded: Number(r?.recorded ?? 0),
        skipped: Number(r?.skipped ?? 0),
        suppressed: Number(r?.suppressed ?? 0),
        refused: Number(r?.refused ?? 0),
        noEmail: Number(r?.no_email ?? 0),
      };
    },
    async emailSendTest({ templateId, companyId = null }) {
      const r = await invoke<SendTestResult>({
        action: "test",
        template_id: id(templateId),
        company_id: companyId == null ? null : id(companyId),
      });
      return {
        ok: true,
        ...(r?.warning ? { warning: r.warning } : {}),
        ...(r?.missing ? { missing: r.missing } : {}),
      };
    },
    async emailSeedDefaultTemplates() {
      const r = await invoke<{ inserted?: number }>({
        action: "seed_templates",
      });
      return { inserted: Number(r?.inserted ?? 0) };
    },
    // GAVION X4: one RPC installs the missing templates it needs (drafts), then the sequences (disabled)
    async emailInstallStarterSequences() {
      const r = await rpc<Partial<StarterInstallResult> | null>(
        "install_starter_sequences",
        { p_sequences: STARTER_SEQUENCES, p_templates: starterTemplates() },
      );
      return {
        inserted: Number(r?.inserted ?? 0),
        skipped: Number(r?.skipped ?? 0),
        missing: Array.isArray(r?.missing) ? r.missing.map(String) : [],
      };
    },
    async emailProviderStatus() {
      return invoke<ProviderStatus>({ action: "status" });
    },
    // GAVION open-pixel: 20261013160000 sequence_step_stats (security invoker)
    async emailDecideStopRequest(requestId, decision) {
      return String(
        await rpc<string>("decide_stop_request", {
          p_id: id(requestId),
          p_decision: decision,
        }),
      );
    },
    async emailSequenceStepStats(sequenceId) {
      const rows = await rpc<Record<string, unknown>[] | null>(
        "sequence_step_stats",
        { p_sequence_id: sequenceId == null ? null : id(sequenceId) },
      );
      return (rows ?? []).map(
        (r): SequenceStepStat => ({
          sequence_id: Number(r.sequence_id),
          step: Number(r.step),
          sent: Number(r.sent ?? 0),
          opened: Number(r.opened ?? 0),
          opened_auto: Number(r.opened_auto ?? 0),
          clicked: Number(r.clicked ?? 0),
          replied: Number(r.replied ?? 0),
        }),
      );
    },
    // GAVION sequence-performance: 20261030120000 sequence_performance (security invoker, one jsonb)
    async emailSequencePerformance(sequenceId) {
      const raw = await rpc<unknown>("sequence_performance", {
        p_sequence_id: id(sequenceId),
      });
      return normalizeSequencePerformance(raw, Number(id(sequenceId)));
    },
  };
}
